/**
 * Merging a business-register hit into a client form that already has values.
 *
 * The rule is one-way: the register may FILL a blank, and may never overwrite
 * something a person put there. That matters most on the clients that came over
 * from the old CRM, where the register is the fastest way to fill the 24 rows
 * with no registry code and the 39 with no e-mail - but where the address a
 * colleague typed by hand is still the better address.
 *
 * Pure on purpose: this is the only part of the edit form with a rule worth
 * testing, and it tests without React or a browser.
 */

/** The register fields the picker can offer. */
export interface RegisterHit {
  name: string;
  regCode: string;
  vatNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
}

/** The subset of the form the register can touch. */
export interface PrefillableForm {
  regCode: string;
  vatNumber: string;
  email: string;
  phone: string;
  website: string;
  addressLine: string;
}

export type PrefillField = keyof PrefillableForm;

export interface PrefillResult {
  /** The form after the merge. */
  next: PrefillableForm;
  /** Which fields the register actually filled, for the operator to see. */
  filled: PrefillField[];
  /** Fields the register knew but did not touch, because they were not empty. */
  kept: PrefillField[];
}

/** Whitespace-only counts as empty; that is what an operator means by blank. */
function isBlank(v: string | null | undefined): boolean {
  return !v || v.trim().length === 0;
}

function clean(v: string | null | undefined): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * Fill the blanks from a register hit, leave everything else alone.
 *
 * Returns the fields it filled and the fields it deliberately did not, so the
 * form can say what happened instead of quietly changing five inputs.
 */
export function prefillFromRegister(form: PrefillableForm, hit: RegisterHit): PrefillResult {
  const offered: Record<PrefillField, string> = {
    regCode: clean(hit.regCode),
    vatNumber: clean(hit.vatNumber),
    email: clean(hit.email),
    phone: clean(hit.phone),
    website: clean(hit.website),
    addressLine: clean(hit.address),
  };

  const next = { ...form };
  const filled: PrefillField[] = [];
  const kept: PrefillField[] = [];

  for (const key of Object.keys(offered) as PrefillField[]) {
    const value = offered[key];
    if (!value) continue; // the register has nothing to say about this one
    if (isBlank(form[key])) {
      next[key] = value;
      filled.push(key);
    } else if (form[key].trim() !== value) {
      kept.push(key);
    }
  }

  return { next, filled, kept };
}

/**
 * What actually changed between the loaded client and the edited form.
 *
 * Sending the whole form back would touch `updatedAt` on every save and would
 * overwrite a field a colleague changed in the meantime with a value this form
 * only ever read. A patch of the changed keys says what the operator did.
 *
 * An emptied field is a real change and is sent as null, not dropped: clearing
 * a wrong phone number has to be possible.
 */
export function changedFields<T extends Record<string, unknown>>(
  original: T,
  edited: Record<keyof T, string>,
  keys: readonly (keyof T)[],
): Partial<Record<keyof T, string | null>> {
  const patch: Partial<Record<keyof T, string | null>> = {};
  for (const key of keys) {
    const before = original[key] == null ? "" : String(original[key]);
    const after = (edited[key] ?? "").trim();
    if (after === before.trim()) continue;
    patch[key] = after === "" ? null : after;
  }
  return patch;
}
